/**
 * API Client Service - Authenticated HTTP client for backend API calls
 */

import { authService } from './authService';
import { logger } from '../utils/logger';

// Uses same env var name as Electron version (with VITE_ prefix)
const API_BASE_URL = import.meta.env.VITE_CORPUS_API_BASE_URL || '';

// Impersonation context for admin users
let currentImpersonation: { userId: string; companyId: string } | null = null;

export function setImpersonationContext(ctx: { userId: string; companyId: string } | null): void {
  logger.debug(ctx ? 'Impersonation context set' : 'Impersonation context cleared');
  currentImpersonation = ctx;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Make an authenticated API request
 */
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const token = authService.getToken();

    if (!token) {
      return {
        success: false,
        error: 'Not authenticated',
        code: 'NOT_AUTHENTICATED'
      };
    }

    const headers = new Headers(options.headers);
    headers.set('Authorization', `Bearer ${token}`);
    headers.set('Content-Type', 'application/json');

    // Add impersonation headers if active
    if (currentImpersonation) {
      headers.set('X-Impersonate-User-Id', currentImpersonation.userId);
      headers.set('X-Impersonate-Company-Id', currentImpersonation.companyId);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    });

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      authService.clearToken();
      // Only redirect if not already on login page
      if (!window.location.pathname.startsWith('/login')) {
        const fullPath = window.location.pathname + window.location.search + window.location.hash;
        window.location.href = `/login?redirect=${encodeURIComponent(fullPath)}`;
      }
      return {
        success: false,
        error: 'Session expired',
        code: 'UNAUTHORIZED'
      };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.detail || errorData.message || `HTTP ${response.status}`,
        code: errorData.code || `HTTP_${response.status}`
      };
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return { success: true };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error: any) {
    logger.error('API request failed:', error);
    return {
      success: false,
      error: error.message || 'Network error',
      code: 'NETWORK_ERROR'
    };
  }
}

// ============ User API ============

export interface UserProfile {
  id: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  company: string;
}

export async function getMe(): Promise<ApiResponse<UserProfile>> {
  return fetchWithAuth<UserProfile>('/me');
}

export interface MicrosoftProfile {
  displayName: string | null;
  photoDataUrl: string | null;
}

/**
 * Get Microsoft profile - deprecated in multi-provider auth
 * Profile data now comes from the backend /me endpoint
 */
export async function getMicrosoftProfile(): Promise<ApiResponse<MicrosoftProfile>> {
  // With multi-provider auth, profile data comes from /me endpoint
  // This function is kept for backward compatibility but returns empty data
  return { success: true, data: { displayName: null, photoDataUrl: null } };
}

/**
 * Get profile photo - deprecated in multi-provider auth
 * Users can upload a profile photo via the profile page instead
 */
export async function getProfilePhoto(): Promise<ApiResponse<{ dataUrl: string }>> {
  // Profile photos are no longer fetched from Microsoft Graph
  return { success: false, error: 'No photo available', code: 'NO_PHOTO' };
}

// ============ Admin Impersonation API ============

export interface Alias {
  user_id: string;
  company_id: string;
  company_name: string;
}

export interface AliasesResponse {
  items: Alias[];
  total: number;
  can_impersonate: boolean;
}

export async function getAdminAliases(): Promise<ApiResponse<AliasesResponse>> {
  return fetchWithAuth<AliasesResponse>('/api/admin/aliases');
}

// ============ Playbook API ============

export interface ClauseListItem {
  id: number;
  name: string;
}

export interface ContractListItem {
  id: number;
  name: string;
}

export interface ClauseDetail {
  id: number;
  name: string;
  acceptable_terms: string;
  unacceptable_terms: string;
  contract_types: Array<{ id: number; name: string }>;
  versions: Array<{
    id: number;
    name: string;
    content: string;
    selection_criteria: string;
    created_at: string;
    templates: Array<{ id: number; name: string; s3_key: string }>;
    precedents: Array<{ id: number; name: string; s3_key: string }>;
  }>;
}

export interface ContractDetail {
  id: number;
  name: string;
  note: string;
  framework_s3_key?: string;
  clause_types: Array<{
    id: number;
    name: string;
    order: number | null;
    versions: Array<{
      id: number;
      name: string;
      content: string;
      selection_criteria: string;
      created_at: string;
    }>;
  }>;
}

export async function getClauses(options?: { skip?: number; limit?: number }): Promise<ApiResponse<ClauseListItem[]>> {
  const params = new URLSearchParams();
  if (options?.skip) params.set('skip', String(options.skip));
  if (options?.limit) params.set('limit', String(options.limit));
  const query = params.toString();
  return fetchWithAuth<ClauseListItem[]>(`/api/playbook/clauses${query ? `?${query}` : ''}`);
}

export async function getClauseDetail(id: number): Promise<ApiResponse<ClauseDetail>> {
  return fetchWithAuth<ClauseDetail>(`/api/playbook/clauses/${id}`);
}

export async function getContracts(options?: { skip?: number; limit?: number }): Promise<ApiResponse<ContractListItem[]>> {
  const params = new URLSearchParams();
  if (options?.skip) params.set('skip', String(options.skip));
  if (options?.limit) params.set('limit', String(options.limit));
  const query = params.toString();
  return fetchWithAuth<ContractListItem[]>(`/api/playbook/contracts${query ? `?${query}` : ''}`);
}

export async function getContractDetail(id: number): Promise<ApiResponse<ContractDetail>> {
  return fetchWithAuth<ContractDetail>(`/api/playbook/contracts/${id}`);
}

export async function deleteClause(id: number): Promise<ApiResponse<void>> {
  return fetchWithAuth<void>(`/api/playbook/clauses/${id}`, {
    method: 'DELETE'
  });
}

export async function deleteContract(id: number): Promise<ApiResponse<void>> {
  return fetchWithAuth<void>(`/api/playbook/contracts/${id}`, {
    method: 'DELETE'
  });
}

// ============ Playbook Update API ============

export interface UpdateClauseRequest {
  name?: string | null;
  acceptable_terms?: string | null;
  unacceptable_terms?: string | null;
}

export async function updateClause(id: number, data: UpdateClauseRequest): Promise<ApiResponse<ClauseDetail>> {
  return fetchWithAuth<ClauseDetail>(`/api/playbook/clauses/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
}

export interface CreateClauseRequest {
  name: string;
  acceptable_terms?: string;
  unacceptable_terms?: string;
}

export async function createClause(data: CreateClauseRequest): Promise<ApiResponse<ClauseDetail>> {
  return fetchWithAuth<ClauseDetail>('/api/playbook/clauses', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export interface ClauseTypeAssociation {
  clause_type_id: number;
  order: number;
  action: 'add' | 'remove' | 'update';
}

export interface UpdateContractRequest {
  name?: string | null;
  note?: string | null;
  clause_type_associations?: ClauseTypeAssociation[];
  framework_s3_key?: string | null;
}

export async function updateContract(id: number, data: UpdateContractRequest): Promise<ApiResponse<ContractDetail>> {
  return fetchWithAuth<ContractDetail>(`/api/playbook/contracts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
}

export interface CreateContractRequest {
  name: string;
  note?: string;
  clause_type_associations?: Array<{
    clause_type_id: number;
    order: number;
  }>;
  framework_s3_key: string;
}

export async function createContract(data: CreateContractRequest): Promise<ApiResponse<ContractDetail>> {
  return fetchWithAuth<ContractDetail>('/api/playbook/contracts', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// ============ Clause Blobs API ============

export interface ClauseBlobsResponse {
  clause_type_id: number;
  clause_type_name: string;
  blob_names: string[];
}

export async function getClauseBlobs(clauseId: number): Promise<ApiResponse<ClauseBlobsResponse>> {
  return fetchWithAuth<ClauseBlobsResponse>(`/api/playbook/clauses/${clauseId}/blobs`);
}

// ============ Clause Version API ============

export interface UpdateClauseVersionRequest {
  name?: string | null;
  content?: string | null;
  selection_criteria?: string | null;
}

export async function updateClauseVersion(id: number, data: UpdateClauseVersionRequest): Promise<ApiResponse<void>> {
  return fetchWithAuth<void>(`/api/playbook/clause-versions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
}

export async function deleteClauseVersion(id: number): Promise<ApiResponse<void>> {
  return fetchWithAuth<void>(`/api/playbook/clause-versions/${id}`, {
    method: 'DELETE'
  });
}

export interface CreateClauseVersionRequest {
  clause_type_id: number;
  name: string;
  content?: string;
  selection_criteria?: string;
}

export async function createClauseVersion(data: CreateClauseVersionRequest): Promise<ApiResponse<void>> {
  return fetchWithAuth<void>('/api/playbook/clause-versions', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// ============ Chat API ============

export interface Conversation {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  role: string;
  content: string;
  timestamp: string;
  tool_calls?: Array<{
    id: string;
    name: string;
    display_name?: string;
    args: Record<string, unknown>;
    status: 'running' | 'success' | 'error';
    result?: string;
  }>;
  attached_files?: Array<{
    name: string;
    size: number;
  }>;
  generated_contract?: {
    content_b64: string;
    filename: string;
    media_type: string;
    size_bytes: number;
  } | null;
}

export async function listConversations(page = 1, pageSize = 100): Promise<ApiResponse<PaginatedResponse<Conversation>>> {
  return fetchWithAuth<PaginatedResponse<Conversation>>(
    `/api/protected/chat/conversations?page=${page}&page_size=${pageSize}`
  );
}

export async function getConversationMessages(
  conversationId: string,
  page = 1,
  pageSize = 100
): Promise<ApiResponse<PaginatedResponse<ChatMessage>>> {
  return fetchWithAuth<PaginatedResponse<ChatMessage>>(
    `/api/protected/chat/conversations/${conversationId}/messages?page=${page}&page_size=${pageSize}`
  );
}

export async function deleteConversation(conversationId: string): Promise<ApiResponse<void>> {
  return fetchWithAuth<void>(`/api/protected/chat/conversations/${conversationId}`, {
    method: 'DELETE'
  });
}

export interface CancelGenerationResponse {
  cancelled: boolean;
  message: string;
}

export async function cancelGeneration(conversationId: string): Promise<ApiResponse<CancelGenerationResponse>> {
  return fetchWithAuth<CancelGenerationResponse>(`/api/protected/chat/conversations/${conversationId}/cancel`, {
    method: 'POST'
  });
}

export interface SendMessageInput {
  conversationId?: string;
  content: string;
  corpusId?: string;
  building_contract_id?: number;
}

export interface SendMessageResponse {
  conversationId: string;
  conversationName?: string;
}

export async function sendChatMessage(input: SendMessageInput): Promise<ApiResponse<SendMessageResponse>> {
  return fetchWithAuth<SendMessageResponse>('/api/chat/send', {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: input.conversationId,
      content: input.content,
      corpus_id: input.corpusId,
      building_contract_id: input.building_contract_id
    })
  });
}

// ============ Generated Contract Download API ============

export interface GeneratedContract {
  content_b64: string;
  filename: string;
  media_type: string;
  size_bytes: number;
}

export function downloadGeneratedContract(contract: GeneratedContract): void {
  const binaryString = atob(contract.content_b64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  const blob = new Blob([bytes], { type: contract.media_type });
  const url = URL.createObjectURL(blob);

  // Pass url as revokeUrl to clean up after download starts
  triggerDownload(url, contract.filename, url);
}

// ============ Upload API ============

export interface GenerateUploadUrlRequest {
  filename: string;
  content_type: string;
  prefix?: string;  // 'frameworks' | 'chat-attachments'
}

export interface GenerateUploadUrlResponse {
  presigned_url: string;
  file_key: string;
}

export async function generateUploadUrl(
  data: GenerateUploadUrlRequest
): Promise<ApiResponse<GenerateUploadUrlResponse>> {
  return fetchWithAuth<GenerateUploadUrlResponse>('/api/upload/generate-upload-url', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export interface UploadToS3Result {
  success: boolean;
  error?: string;
}

export interface UploadToS3Options {
  presignedUrl: string;
  file: File;
  contentType?: string;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

export function uploadToS3(
  presignedUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
  contentType?: string
): Promise<UploadToS3Result> {
  return new Promise((resolve) => {
    // Check if already aborted before starting
    if (signal?.aborted) {
      resolve({ success: false, error: 'Upload cancelled' });
      return;
    }

    const xhr = new XMLHttpRequest();

    // Create named abort handler so we can remove the listener later
    const abortHandler = () => {
      xhr.abort();
    };

    // Helper to cleanup abort listener
    const cleanup = () => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }
    };

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener('load', () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ success: true });
      } else {
        resolve({ success: false, error: `Upload failed: HTTP ${xhr.status}` });
      }
    });

    xhr.addEventListener('error', () => {
      cleanup();
      resolve({ success: false, error: 'Network error during upload' });
    });

    xhr.addEventListener('abort', () => {
      cleanup();
      resolve({ success: false, error: 'Upload cancelled' });
    });

    // Listen for abort signal
    if (signal) {
      signal.addEventListener('abort', abortHandler);
    }

    xhr.open('PUT', presignedUrl);
    // Use contentType from presigned URL generation, fall back to file.type, then generic binary
    const finalContentType = contentType || file.type || 'application/octet-stream';
    xhr.setRequestHeader('Content-Type', finalContentType);
    xhr.send(file);
  });
}

export interface FinalizeUploadRequest {
  file_key: string;
  filename: string;
  content_type: string;
  file_size: number;
}

export interface FinalizeUploadResponse {
  file_key: string;
  status: string;
}

export async function finalizeUpload(
  data: FinalizeUploadRequest
): Promise<ApiResponse<FinalizeUploadResponse>> {
  return fetchWithAuth<FinalizeUploadResponse>('/api/upload/finalize-upload', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// ============ Document Analysis Upload API ============

export interface InitDocumentUploadRequest {
  filename: string;
  file_size: number;
  content_type: string;
  file_hash: string;
}

export interface DocumentInfo {
  id: number;
  filename: string;
  storage_path?: string;
  file_hash?: string;
  file_size?: number;
  content_type?: string;
  status?: string;
  created_at?: string;
  // Fields for existing analyzed documents
  analysis_id?: number;
  analysis_status?: string;
  overall_note?: number;
}

export interface InitDocumentUploadResponse {
  exists: boolean;
  document?: DocumentInfo | null;
  upload_url?: string | null;
  storage_key?: string | null;
  expires_in?: number | null;
  reactivated_document_id?: number | null;
}

export async function initDocumentUpload(
  data: InitDocumentUploadRequest
): Promise<ApiResponse<InitDocumentUploadResponse>> {
  return fetchWithAuth<InitDocumentUploadResponse>('/api/documents/init-upload', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export interface ProcessDocumentRequest {
  storage_key: string;
  filename: string;
  file_hash: string;
  content_type: string;
  file_size: number;
  doc_metadata?: Record<string, unknown>;
  reactivated_document_id?: number | null;
}

export interface ProcessDocumentResponse {
  id: number;
  company_id: string;
  user_id: string;
  filename: string;
  storage_path: string;
  file_hash: string;
  file_size: number;
  content_type: string;
  status: string;
  doc_metadata: Record<string, unknown>;
  content_markdown: string | null;
  created_at: string;
  updated_at: string;
}

export async function processDocument(
  data: ProcessDocumentRequest
): Promise<ApiResponse<ProcessDocumentResponse>> {
  return fetchWithAuth<ProcessDocumentResponse>('/api/documents/process', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// ============ Document Progress SSE API ============

export type ClauseStatus = 'Optimale' | 'Incertaine' | 'Critique' | 'Manquante' | 'Supplementaire';

export interface BaseProgressEvent {
  timestamp: string;
}

export interface ProgressEvent extends BaseProgressEvent {
  type: 'started' | 'progress';
  message: string;
  step?: number;
  total_steps?: number;
  details?: Record<string, unknown>;
}

export interface ClauseResultEvent extends BaseProgressEvent {
  type: 'clause_result';
  id: number;
  document_clause_name: string;
  matched_clause_name?: string;
  matched_clause_id?: number;
  content: string;
  status: ClauseStatus;
  explanation: string;
  reformulation?: string;
}

export interface CompletedEvent extends BaseProgressEvent {
  type: 'completed';
  message?: string;
  details?: {
    expected_clauses_count?: number;
    found_clauses_count?: number;
    extra_clauses_count?: number;
    [key: string]: unknown;
  };
}

export interface ErrorEvent extends BaseProgressEvent {
  type: 'error';
  message: string;
}

export type DocumentProgressEvent = ProgressEvent | ClauseResultEvent | CompletedEvent | ErrorEvent;

/**
 * Subscribe to document processing progress via SSE.
 * Uses native EventSource for proper real-time streaming.
 * Token is passed via query parameter since EventSource doesn't support custom headers.
 */
export async function subscribeToDocumentProgress(
  documentId: number,
  onEvent: (event: DocumentProgressEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const token = authService.getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  return new Promise((resolve, reject) => {
    let url = `${API_BASE_URL}/api/documents/${documentId}/progress?token=${encodeURIComponent(token)}`;
    if (currentImpersonation) {
      url += `&company_id=${encodeURIComponent(currentImpersonation.companyId)}`;
    }
    console.log(`[SSE] Subscribing to document progress - documentId: ${documentId}`);
    console.log(`[SSE] URL: ${url.replace(/token=[^&]+/, 'token=***')}`);

    // Check if already aborted before creating EventSource
    if (signal?.aborted) {
      reject(new Error('SSE connection aborted'));
      return;
    }

    const eventSource = new EventSource(url);

    console.log('[SSE] EventSource connecting...');
    const startTime = Date.now();
    let receivedAnyEvent = false;
    let connectionOpened = false;
    let noEventTimeoutId: ReturnType<typeof setTimeout> | null = null;

    // Named abort handler for proper cleanup
    const onAbort = () => {
      cleanup();
      reject(new Error('SSE connection aborted'));
    };

    const cleanup = () => {
      if (noEventTimeoutId) {
        clearTimeout(noEventTimeoutId);
        noEventTimeoutId = null;
      }
      eventSource.close();
      // Remove abort listener to prevent memory leak
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
      console.log(`[SSE +${((Date.now() - startTime) / 1000).toFixed(2)}s] EventSource closed`);
    };

    // Handle abort signal
    if (signal) {
      signal.addEventListener('abort', onAbort);
    }

    eventSource.onopen = () => {
      connectionOpened = true;
      console.log(`[SSE +${((Date.now() - startTime) / 1000).toFixed(2)}s] Connection opened`);

      // Set a timeout to detect if connection opens but no events are received
      // This helps diagnose cases where the backend doesn't send events
      noEventTimeoutId = setTimeout(() => {
        if (!receivedAnyEvent) {
          console.warn(`[SSE +${((Date.now() - startTime) / 1000).toFixed(2)}s] WARNING: Connection opened but no events received after 10s`);
        }
      }, 10000);
    };

    eventSource.onmessage = (e) => {
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
      console.log(`[SSE +${elapsed}s] Message received`);
      receivedAnyEvent = true;

      try {
        const event = JSON.parse(e.data) as DocumentProgressEvent;
        const stepInfo = 'step' in event && event.step ? `step ${event.step}` : '';
        console.log(`[SSE +${elapsed}s] Event:`, event.type, stepInfo);
        onEvent(event);

        // Close connection on completion or error
        if (event.type === 'completed' || event.type === 'error') {
          cleanup();
          resolve();
        }
      } catch (err) {
        logger.error('Failed to parse SSE event:', e.data);
      }
    };

    eventSource.onerror = (e) => {
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
      console.error(`[SSE +${elapsed}s] EventSource error:`, e);
      cleanup();
      // Only resolve if we received events, otherwise reject to signal connection failure
      if (receivedAnyEvent) {
        resolve();
      } else {
        reject(new Error(`SSE connection failed after ${elapsed}s`));
      }
    };
  });
}

// ============ File Download API ============

/**
 * Trigger a browser download by creating and clicking an anchor element.
 * If revokeUrl is provided, it will be revoked after a short delay to allow the download to start.
 */
function triggerDownload(url: string, filename?: string, revokeUrl?: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || '';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke blob URL after delay to ensure download has started
  if (revokeUrl) {
    setTimeout(() => URL.revokeObjectURL(revokeUrl), 200);
  }
}

export interface GetDownloadUrlResponse {
  download_url: string;
}

export async function getDownloadUrl(
  fileKey: string
): Promise<ApiResponse<GetDownloadUrlResponse>> {
  return fetchWithAuth<GetDownloadUrlResponse>(
    `/api/upload/download-url?file_key=${encodeURIComponent(fileKey)}`,
    { method: 'GET' }
  );
}

export async function downloadFile(
  fileKey: string
): Promise<{ success: boolean; error?: string }> {
  const response = await getDownloadUrl(fileKey);
  if (!response.success || !response.data?.download_url) {
    return { success: false, error: response.error || 'Download URL not available' };
  }

  // Extract filename from the file key (last segment after /)
  const filename = fileKey.split('/').pop() || undefined;
  triggerDownload(response.data.download_url, filename);

  return { success: true };
}

// ============ Notifications API ============

import type {
  Notification,
  NotificationQueryParams,
  NotificationsResponse,
  MarkAsReadResponse
} from '../shared/notification-types';

export type { Notification, NotificationsResponse };

export async function getNotifications(
  params: NotificationQueryParams = {}
): Promise<ApiResponse<NotificationsResponse>> {
  const urlParams = new URLSearchParams();
  if (params.type) urlParams.set('type', params.type);
  if (params.is_read !== undefined) urlParams.set('is_read', String(params.is_read));
  if (params.created_after) urlParams.set('created_after', params.created_after);
  if (params.created_before) urlParams.set('created_before', params.created_before);
  if (params.skip !== undefined) urlParams.set('skip', String(params.skip));
  if (params.limit !== undefined) urlParams.set('limit', String(params.limit));

  const query = urlParams.toString();
  return fetchWithAuth<NotificationsResponse>(
    `/api/playbook/notifications${query ? `?${query}` : ''}`
  );
}

export async function getUnreadNotificationCount(): Promise<ApiResponse<{ count: number }>> {
  // Fetch with is_read=false and limit=1 to get just the unread_count efficiently
  const response = await getNotifications({ is_read: false, limit: 1 });
  if (response.success && response.data) {
    return { success: true, data: { count: response.data.unread_count } };
  }
  return { success: false, error: response.error, code: response.code };
}

export async function markNotificationsAsRead(
  notificationIds: number[]
): Promise<ApiResponse<MarkAsReadResponse>> {
  return fetchWithAuth<MarkAsReadResponse>('/api/playbook/notifications/read', {
    method: 'PATCH',
    body: JSON.stringify({ notification_ids: notificationIds })
  });
}

export async function deleteNotification(id: number): Promise<ApiResponse<void>> {
  return fetchWithAuth<void>(`/api/playbook/notifications/${id}`, {
    method: 'DELETE'
  });
}

// ============ Document Analysis History API ============

export interface AnalysisListItem {
  document_id: number;
  document_name: string;
  overall_note: number;
  status: string;
  created_at: string;
}

export interface AnalysisListResponse {
  analyses: AnalysisListItem[];
  total: number;
}

export interface AnalysisClauseDetail {
  id: number;
  document_clause_name: string;
  matched_clause_name: string | null;
  matched_clause_id: number | null;
  content: string;
  status: string;
  explanation: string;
  reformulation: string | null;
}

export interface AnalysisStatistics {
  total_clauses: number;
  conform_count: number;
  warning_count: number;
  critical_count: number;
  missing_count: number;
  extra_count: number;
}

export interface AnalysisDetailResponse {
  analysis_id: number;
  document_id: number;
  document_name: string;
  contract_type_id: number;
  contract_type_name: string;
  company_role: string;
  parties: string[];
  summary: string;
  directives_status: string;
  directives_explanation: string;
  clauses: AnalysisClauseDetail[];
  overall_note: number;
  statistics: AnalysisStatistics;
  status: string;
  created_at: string;
}

export async function getDocumentAnalyses(
  options?: { skip?: number; limit?: number; getAllCompanyAnalysis?: boolean }
): Promise<ApiResponse<AnalysisListResponse>> {
  const params = new URLSearchParams();
  if (options?.skip !== undefined) params.set('skip', String(options.skip));
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.getAllCompanyAnalysis) params.set('get_all_company_analysis', 'true');

  const query = params.toString();
  return fetchWithAuth<AnalysisListResponse>(
    `/api/documents/analyses${query ? `?${query}` : ''}`
  );
}

export async function getDocumentAnalysisDetail(
  documentId: number
): Promise<ApiResponse<AnalysisDetailResponse>> {
  return fetchWithAuth<AnalysisDetailResponse>(
    `/api/documents/${documentId}/analysis`
  );
}

export interface DeleteDocumentResponse {
  success: boolean;
  document_id: number;
  message: string;
}

export async function deleteDocument(
  documentId: number
): Promise<ApiResponse<DeleteDocumentResponse>> {
  return fetchWithAuth<DeleteDocumentResponse>(
    `/api/documents/${documentId}`,
    { method: 'DELETE' }
  );
}

// ============ Precedents Upload API ============

export interface InitPrecedentUploadRequest {
  filename: string;
  relative_path?: string;
  file_size: number;
  content_type: string;
  file_hash: string;
}

export interface ExistingPrecedentInfo {
  id: number;
  filename: string;
  relative_path?: string;
  status: string;
  created_at: string;
}

export interface InitPrecedentUploadResponse {
  exists: boolean;
  existing_precedent?: ExistingPrecedentInfo;
  upload_url?: string;
  storage_key?: string;
  expires_in?: number;
}

export async function initPrecedentUpload(
  data: InitPrecedentUploadRequest
): Promise<ApiResponse<InitPrecedentUploadResponse>> {
  return fetchWithAuth<InitPrecedentUploadResponse>('/api/precedents/init-upload', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export interface ConfirmPrecedentUploadRequest {
  storage_key: string;
  filename: string;
  relative_path?: string;
  file_hash: string;
  content_type: string;
  file_size: number;
}

export interface ConfirmPrecedentUploadResponse {
  id: number;
  status: string;
}

export async function confirmPrecedentUpload(
  data: ConfirmPrecedentUploadRequest
): Promise<ApiResponse<ConfirmPrecedentUploadResponse>> {
  return fetchWithAuth<ConfirmPrecedentUploadResponse>('/api/precedents/confirm-upload', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export interface ProcessPrecedentResponse {
  id: number;
  status: string;
}

export async function processPrecedent(
  id: number
): Promise<ApiResponse<ProcessPrecedentResponse>> {
  return fetchWithAuth<ProcessPrecedentResponse>(`/api/precedents/${id}/process`, {
    method: 'POST'
  });
}

export interface PrecedentBatchStatusItem {
  id: number;
  status: string;
  error_message?: string;
}

export interface PrecedentBatchStatusResponse {
  precedents: PrecedentBatchStatusItem[];
}

export async function getPrecedentsBatchStatus(
  ids: number[]
): Promise<ApiResponse<PrecedentBatchStatusResponse>> {
  const idsParam = ids.join(',');
  return fetchWithAuth<PrecedentBatchStatusResponse>(
    `/api/precedents/batch-status?ids=${encodeURIComponent(idsParam)}`
  );
}

// ============ Precedents History API ============

export interface PrecedentListItem {
  id: number;
  filename: string;
  relative_path?: string;
  status: string;
  file_size: number;
  created_at: string;
  user_id?: string;
}

export interface PrecedentListResponse {
  precedents: PrecedentListItem[];
  total: number;
}

export async function getPrecedentsList(
  options?: { skip?: number; limit?: number; getAllCompany?: boolean }
): Promise<ApiResponse<PrecedentListResponse>> {
  const params = new URLSearchParams();
  if (options?.skip !== undefined) params.set('skip', String(options.skip));
  if (options?.limit !== undefined) params.set('limit', String(options.limit));
  if (options?.getAllCompany) params.set('get_all_company', 'true');

  const query = params.toString();
  return fetchWithAuth<PrecedentListResponse>(
    `/api/precedents${query ? `?${query}` : ''}`
  );
}

export interface DeletePrecedentResponse {
  success: boolean;
  precedent_id: number;
  message: string;
}

export async function deletePrecedent(
  id: number
): Promise<ApiResponse<DeletePrecedentResponse>> {
  return fetchWithAuth<DeletePrecedentResponse>(
    `/api/precedents/${id}`,
    { method: 'DELETE' }
  );
}
